import { Fragment } from "react/jsx-runtime";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useContext, useEffect, useRef, useState } from "react";
import { ApplicationState } from "../services/context/ApplicationStateContext";
import useProfile from "../services/utils/custom hooks/useProfile";
import dateFormatter from "../services/utils/helpers/dateFormatter";
import PickupAddressFields from "../components/formsFields/pickupAddressFields";
import CustomText from "../components/atomic/CustomText";
import PickupInformation from "../components/formsFields/pickupInformation";
import ProgressBarPerField from "../components/molecular/ProgressBarPerField";
import AdminContentLayout from "../layouts/AdminContentLayout";
import { useHistory } from "react-router-dom";
import AdditionalInformationFields from "../components/formsFields/AdditionalInformationFields";
import toastMessages from "../services/utils/helpers/toastMessage";
import dataConstants from "../constants/dataConstants";
import getKeyByValue from "../services/utils/helpers/keyFromObjectValue";
import axiosInstance from "../services/api";
import { useOktaAuth } from "@okta/okta-react";
import useStateAndCity from "../services/utils/custom hooks/useStateAndCity";
import Button from "../components/organism/Button";
import CustomPopUp from "../components/global/CustomPopup";
import SubHeader from "../components/global/SubHeader";

export const validationSchemas = Yup.object().shape({
  contactName: Yup.string()
    .required("Contact name is required!")
    .test(
      "no-empty-blank-space",
      "Contact name cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  company: Yup.string()
    .required("Company is required!")
    .test(
      "no-empty-blank-space",
      "Company cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  address1: Yup.string()
    .required("Address is required!")
    .test(
      "no-empty-blank-space",
      "Address cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  city: Yup.string()
    .required("City is required!")
    .test(
      "no-empty-blank-space",
      "City cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  state: Yup.string()
    .required("State is required!")
    .test(
      "no-empty-blank-space",
      "State cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  zipCode: Yup.string()
    .min(5, "Zip code length must equal to 5")
    .max(5, "Zip code length must equal to 5")
    .matches(/^[0-9]+$/, "Zip code must be numbers")
    .required("Zip Code is required!"),
  phone: Yup.string()
    .test(
      "phone-validation",
      "Phone must contain 10 digits with the following format 000-000-0000",
      (value) => {
        if (!value) {
          return true;
        }
        return /\d{3}-\d{3}-\d{4}/.test(value);
      }
    )
    .required("Phone is required!"),
  pickupDate: Yup.string()
    .test(
      "custom-date-validator",
      (value: string | undefined) => !!dateFormatter(value)
    )
    .required("Pickup Date is required!"),
  closeTime: Yup.string()
    .required("Close Time is required!")
    .test(
      "no-empty-blank-space",
      "Close Time cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
  readyDateTimestamp: Yup.string()
    .required("Available Ready Time is required!")
    .test(
      "no-empty-blank-space",
      "Available Ready Time cannot be an empty blank space(s)",
      (value: any) => {
        return value && value.trim() !== "";
      }
    ),
});

interface schedulePickupValues {
  contactName: string;
  company: string;
  address1: string;
  address2: string;
  state: string;
  stateValue: string;
  city: string;
  phone: string;
  zipCode: string;
  pickupDate: string;
  closeTime: string;
  readyDateTimestamp: string;
  additionalInformation: string;
  orgId: number;
}

function RenderFields({
  initialValues,
  setFieldValue,
  errors,
  touched,
  setImage,
  isValid,
  handleChange,
  handleBlur,
  values,
  setFieldError,
  validateField,
  setValidFields,
  setScheduleDisabled,
  requiredFields,
  ...rest
}: any): JSX.Element {
  useEffect(() => {
    rest.validateForm(values).then((res: any) => {
      setValidFields(requiredFields - Object.keys(res).length);
    });
  }, [values]);
  return (
    <Fragment>
      <div className="focus:outline-primary shadow-sm border-[0.5px] p-6 my-5 rounded-md hover:border-primary hover:border">
        <CustomText text={"Pickup Address"} fontWeight="font-semibold" />
        <div className="flex flex-wrap justify-between mb-3 w-full">
          <PickupAddressFields
            values={values}
            touched={touched}
            setFieldValue={setFieldValue}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            {...rest}
          />
        </div>
      </div>
      <div className="flex flex-wrap justify-between mb-3 focus:outline-primary w-full p-6 my-5 shadow-sm border-[0.5px] rounded-md hover:border-primary hover:border">
        <PickupInformation
          values={values}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      </div>
      <div className="flex flex-wrap justify-between mb-3 focus:outline-primary w-full p-6 my-5 shadow-sm border-[0.5px] rounded-md hover:border-primary hover:border">
        <CustomText
          text={"Additional Information"}
          fontWeight="font-semibold"
        />

        <AdditionalInformationFields
          values={values}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      </div>
    </Fragment>
  );
}

export default function SchedulePickup() {
  const progressPercentage = "0%";
  const {
    state: {
      selectedOrganizationData,
      selectedPathlabData,
      selectedOrganization,
    },
  } = useContext(ApplicationState);

  const {
    data: profileData,
    isLoading: isLoadingProfile,
    isError: profileError,
  } = useProfile();
  let isPLU = profileData?.user?.roles?.includes("PathLab User");
  let OrganisationORPathlab = isPLU
    ? selectedPathlabData
    : selectedOrganizationData;
  const [validFields, setValidFields] = useState<number>(0);
  const [previousOrgId, setPreviousOrgId] = useState<any>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [previousPathLabId, setPreviousPathLabId] = useState<any>(null);
  const maskedPhone = (phone: string) => {
    const phoneString = phone.replace(/-/g, "");
    return `${phoneString?.[0]}${phoneString?.[1]}${phoneString?.[2]}-${phoneString?.[3]}${phoneString?.[4]}${phoneString?.[5]}-${phoneString?.[6]}${phoneString?.[7]}${phoneString?.[8]}${phoneString?.[9]}`;
  };
  const contactName = `${profileData?.user?.firstName} ${profileData?.user?.lastName}`;
  const history = useHistory();
  const { authState } = useOktaAuth();
  const date = new Date();
  const requiredFields = 10;
  const { data: states } = useStateAndCity();
  const formattedDate = `${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date
    .getDate()
    .toString()
    .padStart(2, "0")}-${date.getFullYear()}`;
  const formRef = useRef<any>();
  const [orgPopup, setOrgPopup] = useState<boolean>(false);

  const initialValues: schedulePickupValues = {
    contactName: contactName ? contactName : "",
    company: OrganisationORPathlab?.name ? OrganisationORPathlab?.name : "",
    address1: OrganisationORPathlab?.address
      ? OrganisationORPathlab?.address
      : "",
    address2: OrganisationORPathlab?.address2
      ? OrganisationORPathlab?.address2
      : "",
    state: OrganisationORPathlab?.state ? OrganisationORPathlab?.state : "",
    stateValue: OrganisationORPathlab?.state
      ? OrganisationORPathlab?.state
      : "",
    city: OrganisationORPathlab?.city ? OrganisationORPathlab?.city : "",
    phone: OrganisationORPathlab?.phone
      ? maskedPhone(OrganisationORPathlab?.phone)
      : "",
    zipCode: OrganisationORPathlab?.zipcode
      ? OrganisationORPathlab?.zipcode
      : "",
    pickupDate: formattedDate,
    closeTime: "",
    readyDateTimestamp: "",
    additionalInformation: "",
    orgId: OrganisationORPathlab?.id,
  };

  function handlePopupClose() {
    setOrgPopup(false);
    history.replace("/access/shipments/pick-ups");
  }
async function schedulePickup(data: schedulePickupValues) {
    setSubmitting(true);
    let formattedData: any = { ...data };

    // --- DATE CALCULATION LOGIC ---
    const readyDateTimeInHHMMSS = data.readyDateTimestamp;
    const year = data.pickupDate?.split("-")[2];
    const month = data.pickupDate?.split("-")[0];
    const day = data.pickupDate?.split("-")[1];
    const hour = readyDateTimeInHHMMSS?.split(":")[0];
    const minute = readyDateTimeInHHMMSS?.split(":")[1];

    formattedData.readyDateTimestamp = new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute)
      )
    );

    formattedData.closeTime = getKeyByValue(
      dataConstants.TimeKey,
      data.closeTime
    )
      ? getKeyByValue(dataConstants.TimeKey, data.closeTime)
      : data.closeTime;

    if (formattedData.state !== formattedData.stateValue) {
      formattedData.state = formattedData.stateValue;
    }
    delete formattedData.stateValue;
    
    // 1. Try to find the ID from multiple sources 
    const resolvedOrgId = 
      OrganisationORPathlab?.id ||         
      selectedOrganization ||           
      selectedOrganizationData?.id ||   
      selectedPathlabData?.id    

    // 2. Assign the best match found
    formattedData.orgId = resolvedOrgId;

    // 4. BLOCKER: If we still don't have an ID, stop here.
    if (!formattedData.orgId) {
      toastMessages.error("Organization ID is missing. Please refresh or select the organization again.");
      setSubmitting(false);
      return; // <--- EXIT FUNCTION, DO NOT CALL API
    }

    try {
      const response: any = await axiosInstance.post(
        "/v1/fedex/schedule-pickup/",
        { ...formattedData }, // We send formattedData, NOT data
        {
          headers: {
            Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
          },
        }
      );

      const { statusCode, message } = response;

      if (response?.data?.message === "Success") {
        toastMessages.success("Pickup scheduled successfully!");
        history.push("/access/shipments/pick-ups");
      }

      setSubmitting(false);
    } catch (error: any) {
      setSubmitting(false);
      if (
        error?.response?.data?.errors[0]?.message ===
        "StateOrProvinceCode is missing."
      ) {
        toastMessages?.error("Please enter valid zip code.");
      } else if (error?.response?.data?.errors[0]?.message) {
        toastMessages.error(error?.response?.data?.errors[0]?.message);
      } else {
        toastMessages.error(error?.message);
      }
    }
  }

function Render({
  errors,
  touched,
  setFieldValue,
  isValid,
  values,
  initialValues,
  setFieldError,
  validateField,
  ...rest
}: any): JSX.Element {
  const [scheduleDisabled, setScheduleDisabled] = useState<boolean>(true);

  return (
    <AdminContentLayout
      pageTitle="Shipments | CSTL"
      subHeaderTitle="Shipments"
      hideSubHeaderSelectors
      showButtonInSubheaderForMobile
      redirect={() => history.goBack()}
      customButtons={[
        {
          buttonTheme: "primary",
          textInformation: {
            text: "Schedule Pickup",
            fontWeight: "font-semibold",
          },
          onClick: () => formRef?.current?.handleSubmit(),
          type: "submit",
          className: "mr-6",
          disabled: !isValid || submitting,
          loading: submitting,
        },
      ]}
    >
      <div className="  bg-white  px-2 overflow-auto">
        
        {/* --- FIXED SECTION --- */}
      <div className="px-6 sticky top-0 h-16 pt-3 z-20 bg-white shadow-sm">
      <ProgressBarPerField
        validFields={validFields}
        totalFields={requiredFields}
      />
    </div>
        {/* --------------------- */}

        <Form className="col-span-4 flex flex-col w-full lg:px-[109px]">
          <RenderFields
            initalValues={initialValues}
            values={values}
            isPage={false}
            setScheduleDisabled={setScheduleDisabled}
            errors={errors}
            touched={touched}
            isValid={isValid}
            setFieldValue={setFieldValue}
            setFieldError={setFieldError}
            requiredFields={requiredFields}
            validateField={validateField}
            setValidFields={setValidFields}
            {...rest}
          />
        </Form>
      </div>
    </AdminContentLayout>
  );
}
  return (
    <Fragment>
      {!selectedOrganization && !isPLU && (
        <CustomPopUp
          hidePopUp={() => handlePopupClose()}
          popUpTitle={"Shipments"}
          onlineOrder={false}
          hideClose={false}
        >
          <Fragment>
            <CustomText
              text="Please select an organization before proceeding"
              fontWeight="font-normal"
              className="text-red-500"
            />

            <div className="flex items-end  justify-end mt-3">
              <Button
                buttonTheme="primary"
                textInformation={{
                  text: `OK`,
                  fontWeight: "font-semibold",
                }}
                onClick={() => handlePopupClose()}
              />
            </div>
          </Fragment>
        </CustomPopUp>
      )}
      <Formik
        enableReinitialize={true}
        validationSchema={validationSchemas.shape({
          ...validationSchemas.fields,
          stateValue: Yup.string().test(
            "is-valid-state",
            "State must be selected from the list",
            (value) => {
              const state = states?.find(
                (state: any) => state.state_code === value
              );
              return !!state;
            }
          ),
        })}
        initialValues={initialValues}
        onSubmit={schedulePickup}
        innerRef={formRef}
        initialStatus={false}
        isInitialValid={false}
      >
        {Render}
      </Formik>
    </Fragment>
  );
}
